/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback, ReactNode, memo } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { ThemeContext, generateTheme } from '../../../Context/ThemeContext';
import { navItems } from './navConfig';
import { DarkModeToggle, HamburgerMenu, SideSidebar, TopSidebar, MobileSidebar, SidebarAnimations } from './Components';

// SIDE BAR
export interface Props {
  children?: ReactNode;
}

// Position toggle component with proper accessibility
const PositionToggle: React.FC<{
  isTopLayout: boolean;
  onToggle: () => void;
  theme: ReturnType<typeof generateTheme>;
}> = memo(({ isTopLayout, onToggle, theme }) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onToggle();
      }
    },
    [onToggle]
  );

  return (
    <div style={{
      position: 'fixed',
      top: '12px',
      right: '12px',
      zIndex: 1001,
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      background: theme.card,
      padding: '6px 12px',
      borderRadius: '6px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      border: `1px solid ${theme.border}`,
      transition: 'all 0.3s ease',
      animation: 'sidebarFadeIn 0.5s ease',
    }}>
      <span
        id="position-toggle-label"
        style={{ fontSize: '12px', fontWeight: 600, color: theme.text }}
      >
        Top Bar
      </span>
      <button
        onClick={onToggle}
        onKeyDown={handleKeyDown}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        role="switch"
        aria-checked={isTopLayout}
        aria-labelledby="position-toggle-label"
        style={{
          width: '36px',
          height: '20px',
          background: isTopLayout ? '#2563eb' : '#475569',
          borderRadius: '10px',
          position: 'relative',
          cursor: 'pointer',
          transition: 'background 0.3s ease',
          boxShadow: isTopLayout
            ? '0 0 8px rgba(37,99,235,0.4)'
            : '0 0 4px rgba(0,0,0,0.2)',
          border: 'none',
          padding: 0,
        }}
      >
        <div
          style={{
            width: '16px',
            height: '16px',
            background: '#fff',
            borderRadius: '50%',
            position: 'absolute',
            top: '2px',
            left: isTopLayout ? '18px' : '2px',
            transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.2s ease',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            transform: isHovered ? 'scale(1.1)' : 'scale(1)',
          }}
          aria-hidden="true"
        />
      </button>
    </div>
  );
});

PositionToggle.displayName = 'PositionToggle';

const Sidebar: React.FC<Props> = memo(({ children }) => {
  const [darkMode, setDarkMode] = useState<boolean>(true);
  const [sidebarPosition, setSidebarPosition] = useState<'side' | 'top'>('side');
  const [windowWidth, setWindowWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});

  const location = useLocation();

  // Window resize handler
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-expand nav items with active children
  useEffect(() => {
    navItems.forEach(item => {
      if (item.children) {
        const hasActiveChild = item.children.some(
          child => location.pathname === child.path || location.pathname.startsWith(child.path + '/')
        );
        if (hasActiveChild) {
          setExpandedItems(prev => ({ ...prev, [item.label]: true }));
        }
      }
    });
  }, [location.pathname]);

  // Responsive breakpoints
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth < 1024;
  const isTopLayout = sidebarPosition === 'top' && !isMobile;

  // Layout dimensions
  const sidebarWidth = collapsed ? 70 : 200;
  const topbarHeight = 60;

  // Theme
  const theme = generateTheme(darkMode);
  const toggleDarkMode = useCallback(() => setDarkMode(prev => !prev), []);
  const toggleSidebarPosition = useCallback(() =>
    setSidebarPosition(prev => prev === 'side' ? 'top' : 'side'), []);

  // Theme context value
  const themeContextValue = {
    darkMode,
    setDarkMode,
    toggleDarkMode,
    theme,
    isMobile,
    isTablet,
    windowWidth,
    sidebarPosition,
  };

  // Prevent body scroll on side layout (desktop)
  useEffect(() => {
    if (!isMobile && sidebarPosition === 'side') {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobile, sidebarPosition]);

  return (
    <ThemeContext.Provider value={themeContextValue}>
      {/* Full page wrapper */}
      <div style={{
        minHeight: '100vh',
        background: theme.bgGradient,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        transition: 'background 0.5s ease',
      }}>

        {/* Position Toggle (PC only) */}
        {!isMobile && (
          <PositionToggle
            isTopLayout={isTopLayout}
            onToggle={toggleSidebarPosition}
            theme={theme}
          />
        )}

        {/* Sidebar/Topbar - Conditional rendering */}
        {isMobile ? (
          <MobileSidebar
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            expandedItems={expandedItems}
            setExpandedItems={setExpandedItems}
          />
        ) : isTopLayout ? (
          <TopSidebar
            topbarHeight={topbarHeight}
            expandedItems={expandedItems}
            setExpandedItems={setExpandedItems}
            isMobile={isMobile}
            setSidebarOpen={setSidebarOpen}
          />
        ) : (
          <SideSidebar
            collapsed={collapsed}
            setCollapsed={setCollapsed}
            sidebarWidth={sidebarWidth}
            expandedItems={expandedItems}
            setExpandedItems={setExpandedItems}
            isMobile={isMobile}
            setSidebarOpen={setSidebarOpen}
          />
        )}

        {/* Main Content */}
        <main
          style={{
            marginLeft: isMobile ? 0 : isTopLayout ? 0 : `${sidebarWidth}px`,
            paddingTop: isTopLayout ? `${topbarHeight}px` : 0,
            height: !isMobile && !isTopLayout ? '100vh' : 'auto',
            overflowY: !isMobile && !isTopLayout ? 'auto' : 'visible',
            padding: isMobile
              ? '12px'
              : isTopLayout
                ? `${topbarHeight + 16}px 24px 16px 24px`
                : '16px 24px',
            boxSizing: 'border-box',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {/* Mobile Header */}
          {isMobile && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px',
              background: theme.sidebar,
              marginBottom: '16px',
              borderRadius: '8px',
              animation: 'sidebarFadeIn 0.4s ease',
            }}>
              <HamburgerMenu
                open={sidebarOpen}
                onClick={() => setSidebarOpen(true)}
              />
              <span style={{ color: '#fff', fontWeight: 600 }}>Admin</span>
              <DarkModeToggle
                darkMode={darkMode}
                toggleDarkMode={toggleDarkMode}
                includeLabel={false}
              />
            </div>
          )}

          {children || <Outlet />}
        </main>

        {/* Global Animations */}
        <SidebarAnimations />
      </div>
    </ThemeContext.Provider>
  );
});

Sidebar.displayName = 'Sidebar';

export default Sidebar;
